/**
 * Numéro 7 du programme pédagogique ETM — Les EPI
 *
 * Format : numéro magazine + QCM final auto-évaluant.
 * Ce contenu est DISTINCT de l'article SEO /magazine/equipement-complet-motard-2026
 * qui reste destiné au référencement Google. Ici, le but est purement pédagogique :
 * comprendre les EPI avant de pouvoir les valider via QCM.
 *
 * Structure-type d'un numéro (réutilisable pour les 8 autres) :
 *  - meta : slug, numéro, thème ETM, image, durée de lecture
 *  - edito : court paragraphe d'intro qui pose l'enjeu
 *  - sections : liste alternée { lesson, didyouknow, trap, tip, story }
 *  - keyStat : chiffre marquant pour ancrer dans la mémoire
 *  - glossary : 5-10 termes techniques
 *  - quiz : config du QCM final (thème, taille, score requis)
 */

import { ShieldCheck, AlertTriangle, Lightbulb, Quote, BookOpen, Users } from 'lucide-react';

export const epiEtmIssue = {
  // META
  slug: 'epi',
  themeCode: 'P', // mappe sur les questions ETM thème "Protection"
  issueNumber: 7,
  totalIssues: 9,
  category: 'Les équipements',
  shortTitle: 'Les EPI',
  title: 'Casque, gants, blouson : non négociable',
  kicker: 'Numéro 7 du programme pédagogique ETM',
  subtitle:
    "Tout ce que tu dois savoir sur les Équipements de Protection Individuelle avant de passer ton ETM — du minimum légal jusqu'à l'équipement qui te sauve vraiment la vie.",
  heroImage: '/img/magazines/epi.jpg',
  heroImageAlt: 'Motarde portant un blouson en cuir, casque intégral, gants et bottes — équipement de protection complet',
  readTimeMin: 12,

  // SEO
  seoDescription:
    "Comprendre les EPI moto avant l'ETM 2026 : casque ECE 22-06, gants EN 13594, blouson EN 17092, dorsale EN 1621-2, bottes EN 13634, airbag. Lecture pédagogique + QCM final.",
  seoKeywords:
    'EPI moto ETM, casque ECE 22-06, gants EN 13594, blouson EN 17092, dorsale EN 1621-2, formation moto',

  // ÉDITO (intro pédagogique courte)
  edito: {
    title: 'Pourquoi ce numéro va peut-être te sauver la vie',
    body: "Dans 70 % des accidents moto, le motard porte un casque mais pas le reste. Pourtant, dans une chute à 50 km/h sans pantalon adapté, le bitume agit comme une râpe à fromage : les brûlures sont profondes, parfois greffées. Ce numéro te donne <strong>en 12 minutes</strong> tout ce qui distingue le minimum légal de l'équipement qui te ramène entier chez toi. À la fin, un QCM de 10 questions te dira si tu as compris l'essentiel.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  // SECTIONS PÉDAGOGIQUES (alternées : lesson / didyouknow / trap / tip / story)
  sections: [
    // === SECTION 1 — La loi (minimum légal) ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'La loi française : 2 EPI obligatoires, pas un de plus',
      paragraphs: [
        "L'idée reçue selon laquelle « il faut » s'équiper de la tête aux pieds pour rouler est… fausse au sens strict. En France, seuls <strong>2 équipements</strong> sont rendus obligatoires par le Code de la route :",
        "<strong>1. Le casque homologué ECE 22-06</strong> (article R431-1). Pour le conducteur ET le passager. La sanction en cas de défaut : 135 € d'amende + 3 points retirés. Le casque doit porter une étiquette blanche avec le numéro d'homologation et son code de référence.",
        "<strong>2. Les gants moto homologués EN 13594</strong> (article R431-1-2, en vigueur depuis novembre 2016). Marquage CE + niveau de protection 1 ou 2 obligatoire. Sanction : 68 € + 1 point retiré.",
        "Tout le reste — blouson, pantalon, bottes, dorsale, airbag — n'est <strong>pas légalement obligatoire</strong>. Mais comme tu vas le voir au chapitre 2, ne pas les porter, c'est risquer beaucoup pour économiser peu.",
      ],
      keyPoints: [
        'Casque ECE 22-06 + gants EN 13594 = seules obligations légales',
        'Le marquage CE + le niveau (1 ou 2) doivent être visibles sur les gants',
        'En cas de chute sans gants : amende ET sanction médicale (mains détruites)',
      ],
    },

    // === SAVIEZ-VOUS ? ===
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "La norme <strong>ECE 22-06</strong> est entrée en vigueur en 2021. Elle remplace l'ancienne ECE 22-05. Différence : les nouveaux casques sont testés à <strong>plus de 8 points d'impact</strong> (contre 5 avant) ET sur des chutes obliques (rotation du crâne). Si ton casque est marqué ECE 22-05, il reste légal — mais les fabricants ne peuvent plus en commercialiser de nouveaux depuis janvier 2024.",
    },

    // === SECTION 2 — Les recommandés ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'Les 4 EPI qui te ramènent entier (fortement recommandés)',
      paragraphs: [
        "Ces équipements ne sont pas obligatoires devant la loi, mais ils le sont devant le bitume. Tout motard expérimenté te le dira : à la première chute, on comprend pourquoi.",
        "<strong>Le blouson moto homologué (EN 17092)</strong> existe en 4 niveaux : AAA (le plus protecteur, type circuit), AA (équilibré, route quotidienne), A (basique, ville), B (sans coques) et C (sans protection certifiée). Le bon compromis : niveau <strong>AA</strong>, équipé de coques aux épaules et aux coudes (norme EN 1621-1).",
        "<strong>La dorsale (EN 1621-2)</strong> protège la colonne vertébrale. Niveau 1 = usage loisir, niveau 2 = circuit. Selon les études IRTAD, elle évite <strong>jusqu'à 90 %</strong> des lésions médullaires en cas de chute sur le dos. C'est l'EPI au meilleur rapport prix / vie sauvée.",
        "<strong>Le pantalon moto (EN 17092)</strong> protège les hanches, fémurs et genoux. Beaucoup roulent en jean classique : à 50 km/h, un jean ordinaire est <strong>déchiré en 0,3 seconde</strong>. Pour la ville, un jean moto homologué (Kevlar ou Cordura) avec coques genoux EN 1621-1 fait le job.",
        "<strong>Les bottes moto (EN 13634)</strong> protègent la cheville, le pied, et limitent l'écrasement. Hauteur mi-mollet + semelle anti-perforation + protection malléoles. À défaut, des baskets renforcées homologuées niveau 1.",
      ],
      keyPoints: [
        'Blouson : niveau AA + coques EN 1621-1 minimum',
        'Dorsale : indispensable, sauve 90 % des lésions médullaires',
        'Pantalon : un jean ordinaire ne protège PAS de la friction bitume',
        'Bottes ou baskets renforcées : cheville la plus exposée en chute',
      ],
    },

    // === PIÈGE À ÉVITER ===
    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« Mon blouson est en cuir, donc je suis protégé. » <strong>FAUX.</strong> Un blouson en cuir non homologué peut être moins protecteur qu'un blouson textile EN 17092 niveau AA. Le cuir doit avoir une épaisseur minimale (1,1 à 1,4 mm) et surtout intégrer des <strong>coques certifiées</strong> aux zones d'impact. Cherche TOUJOURS le marquage CE et le code EN 17092 sur l'étiquette intérieure.",
    },

    // === SECTION 3 — Airbag (techno) ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 3',
      title: "L'airbag moto : la révolution silencieuse",
      paragraphs: [
        "Depuis 2018, les airbags moto se démocratisent. Deux technologies coexistent :",
        "<strong>Airbag à cordon (filaire)</strong> : un cordon relie le motard à la moto. En cas d'éjection, le cordon arraché déclenche le gonflage en 60 millisecondes. Avantages : fiabilité mécanique, prix abordable (200-400 €). Inconvénient : tu peux oublier de t'attacher.",
        "<strong>Airbag électronique (capteurs)</strong> : des capteurs gyroscopiques détectent la chute. Plus précis (déclenchement même sans éjection), mais 500-800 € et batterie à recharger.",
        "Tous deux respectent la norme <strong>EN 1621-4</strong>. D'après les études motard pro, l'airbag réduit de <strong>30 à 40 %</strong> les lésions thoraciques mortelles. C'est aujourd'hui l'équipement avec le meilleur ROI en matière de survie.",
      ],
      keyPoints: [
        'Airbag à cordon = mécanique, fiable, 200-400 €',
        'Airbag électronique = autonome, 500-800 €',
        "Norme commune : EN 1621-4",
        "Réduit de 30 à 40 % les lésions thoraciques mortelles",
      ],
    },

    // === CONSEIL DU MONITEUR ===
    {
      type: 'tip',
      icon: Quote,
      title: 'Le conseil du moniteur ECSR',
      body: "« Quand un élève me demande quoi prioriser dans son budget équipement, je donne TOUJOURS le même ordre : <strong>1. casque qualité moyen-haut</strong> (compter 250-400 €, surtout pas <150 €), <strong>2. dorsale EN 1621-2 niveau 1</strong> (50-80 €), <strong>3. gants homologués</strong> (40-100 €), <strong>4. blouson AA avec coques</strong> (150-300 €). Le pantalon vient après. L'airbag, c'est l'investissement ultime — mais s'il faut sacrifier autre chose pour l'avoir, sacrifie. »",
    },

    // === TÉMOIGNAGE ===
    {
      type: 'story',
      icon: Users,
      title: "Le témoignage : « J'ai compris à la première chute »",
      body: "« J'avais 19 ans, mon permis A2 depuis 6 mois. Je roulais en T-shirt et jean cet été-là, par 35°C. Une voiture m'a coupé la route à 40 km/h. J'ai glissé 8 mètres sur l'asphalte. Résultat : épaule en sang, brûlures du 2e degré sur tout le côté gauche, 3 semaines d'arrêt et 4 mois de cicatrices. Mon casque m'a sauvé. Mais le reste, je l'avais sacrifié pour 5 degrés de moins. Aujourd'hui, je roule TOUJOURS en blouson ventilé. Le confort n'est pas une excuse. » <em>— Mathieu, 23 ans</em>",
    },
  ],

  // CHIFFRE MARQUANT (infographie clé)
  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '70 %',
    middle: 'des motards',
    label: "ont chuté au moins une fois dans leur première année de permis",
    source: 'Étude accidentologie deux-roues — ONISR + ECSR 2024',
    accent: '#cf2031',
  },

  // GLOSSAIRE DES TERMES TECHNIQUES
  glossary: [
    { term: 'ECE 22-06', def: "Norme européenne d'homologation des casques moto entrée en vigueur en 2021. Remplace l'ancienne ECE 22-05. Tests d'impact étendus + chutes obliques." },
    { term: 'EN 13594', def: 'Norme européenne des gants moto. Marquage CE obligatoire + indication du niveau de protection (1 ou 2). Obligatoire en France depuis novembre 2016.' },
    { term: 'EN 17092', def: 'Norme européenne des vêtements moto (blouson, pantalon). 4 niveaux : AAA (max), AA (équilibré), A (basique), B (sans coques), C (sans protection).' },
    { term: 'EN 1621-1', def: "Norme des coques de protection souples intégrées aux blousons et pantalons (épaules, coudes, hanches, genoux). Niveau 1 ou 2." },
    { term: 'EN 1621-2', def: "Norme spécifique des dorsales (protection dos). Niveau 1 (loisir) ou niveau 2 (sport / circuit)." },
    { term: 'EN 13634', def: 'Norme européenne des bottes moto. Évalue la hauteur, la rigidité de la tige et la résistance à la perforation.' },
    { term: 'EN 1621-4', def: "Norme des airbags moto (filaires ou électroniques). Évalue la rapidité de déclenchement et l'efficacité de l'absorption d'énergie." },
    { term: 'AAA / AA / A', def: 'Sous-classements de la norme EN 17092. AAA = protection maximale (circuit). AA = polyvalence route. A = ville / basique.' },
    { term: 'Coque', def: 'Élément rigide ou souple intégré dans un blouson/pantalon, certifié EN 1621-1, qui absorbe l\'énergie d\'un impact ponctuel.' },
    { term: 'ROI sécurité', def: "Rapport entre l'investissement (prix de l'EPI) et le bénéfice (vies sauvées, lésions évitées). La dorsale a le meilleur ROI sécurité du marché." },
  ],

  // CONFIG DU QCM FINAL
  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur les EPI tirées de la banque officielle ETM. Tu dois en réussir <strong>au moins 8 sur 10</strong> pour valider ta maîtrise du thème. Sinon, on te recommandera de re-lire les chapitres sur lesquels tu as buté.",
    themeCode: 'P', // mappe sur le thème "Les équipements de protection"
    questionsCount: 10,
    passingScore: 8,
  },

  // NUMÉRO SUIVANT (cross-link pédagogique)
  nextIssue: {
    slug: 'mecanique',
    number: 6,
    title: 'Une moto bien réglée, c\'est ta vie',
    short: 'La mécanique & l\'entretien',
  },
};

export default epiEtmIssue;
